import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import {PMW_COLORS_NEUTRAL, PMW_COLORS_PRIMARY} from '@Utils/color.util';
import type {BasicComponentCommandProps} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';

const updateSliderStyles = (slider: HTMLInputElement): void => {
  const val = slider.value as unknown as number;
  const min = slider.min as unknown as number;
  const max = slider.max as unknown as number;
  const percentage = ((val - min) / (max - min)) * 100;
  const color = `linear-gradient(90deg, ${PMW_COLORS_PRIMARY.PRIMARY_100} ${percentage}%, ${PMW_COLORS_NEUTRAL.NEUTRAL_6} ${percentage}%) !important`;

  $(slider).attr('style', `background: ${color}`);
};

interface UpdateSliderValueOpts {
  inputElements: HTMLInputElement[];
  property: string;
  value: string;
}

/**
 * Style related commands
 */
export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const cmds = editor.Commands;

  const CMDS = window.PMW.emails.commands;

  /**
   * @param {object} opts
   * @param {string} opts.property
   * @param {number} opts.value
   * @param {HTMLElement[]} [opts.inputElements] HTMLElement to use
   */
  cmds.add(CMDS.UPDATE_SLIDER_VALUE, (editorInstance: Editor, sender: Component, opts: UpdateSliderValueOpts) => {
    const inputs: Array<HTMLInputElement> = [];

    if (opts.inputElements) {
      inputs.push(...opts.inputElements);
    } else {
      const propertyInputs = document.querySelectorAll(`.js-emails-${opts.property}-input`);

      // eslint-disable-next-line
      // @ts-ignore
      inputs.push(...propertyInputs);
    }

    inputs.forEach((inputEl: HTMLInputElement) => {
      inputEl.value = opts.value;
      if (inputEl.type === 'range') {
        updateSliderStyles(inputEl);
      }
    });
  });

  cmds.add(CMDS.MAKE_STYLEMANAGER_ACTIVE, (editorInstance: Editor) => {
    const styleManagerEl = $('.html-editor-style-manager');
    const isActive = styleManagerEl.hasClass('-active');
    const isEmailContainerSelected = editorInstance.getSelected()?.is(window.PMW.emails.components.container.PMW_WRAPPER);

    if (isActive) {
      return;
    }

    styleManagerEl.addClass('-active');
    $('.js-show-hide-stylemanager-icon').removeClass('icon-sidebar-show -make-active').addClass('icon-sidebar-hide -make-inactive');

    if (!isEmailContainerSelected) {
      editor.runCommand(window.PMW.emails.commands.SCROLL_TO_COMPONENT, {delay: 300});
    }
  });

  cmds.add(CMDS.SET_CUSTOM_WIDTH_STYLE, (editorInstance: Editor, sender: Component, opts: {component: Component}) => {
    const sliderEl = $('.pmw-slider-text.js-emails-width-input') as JQuery<HTMLInputElement>;
    const widthValue = sliderEl[0].value;
    const comp = opts.component;

    if (comp && widthValue) {
      comp.removeClass(window.PMW.emails.AUTO_WIDTH_CTA_CLASS);

      editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'width', value: `${widthValue}%`, component: comp});
      editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'button-width', value: 'custom', component: comp});

      let display = 'block';
      let minWidth = 'fit-content';

      if (comp.is(window.PMW.emails.components.text.PMW_BUTTON) && !editorInstance.runCommand(window.PMW.emails.commands.IS_COMPONENT_WIDTH_UNSET, {component: comp})) {
        display = 'table';
        minWidth = 'auto';
      }

      editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'display', value: display, component: comp});
      editorInstance.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
        property: 'min-width',
        value: minWidth,
        id: comp.ccid,
        deviceID: window.PMW.emails.DEVICE_TABLET,
        cssPrefix: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
      });

      editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
        property: 'min-width',
        value: minWidth,
        id: comp.ccid,
        deviceID: window.PMW.emails.DEVICE_MOBILE,
        cssPrefix: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
      });
    }
  });

  cmds.add(CMDS.REFRESH_MEDIA_QUERIES, (editorInstance: Editor, sender: Component, opts: {component: Component; responsiveStyles: string[]}) => {
    const component = opts.component;
    const responsiveStyles = opts.responsiveStyles;

    if (!component || responsiveStyles.length === 0) {
      return;
    }

    responsiveStyles.forEach((style) => {
      const mobileStyle = component.getStyle(window.PMW.emails.MOBILE_CSS_PROP_PREFIX + style);
      const tabletStyle = component.getStyle(window.PMW.emails.TABLET_CSS_PROP_PREFIX + style);

      if (mobileStyle) {
        editorInstance.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
          property: style,
          value: mobileStyle,
          id: component.ccid,
          deviceID: window.PMW.emails.DEVICE_MOBILE,
          cssPrefix: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
        });
      }

      if (tabletStyle) {
        editorInstance.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
          property: style,
          value: tabletStyle,
          id: component.ccid,
          deviceID: window.PMW.emails.DEVICE_TABLET,
          cssPrefix: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
        });
      }
    });

    component.components().forEach((child) => {
      editorInstance.runCommand(CMDS.REFRESH_MEDIA_QUERIES, {component: child, responsiveStyles: responsiveStyles});
    });
  });

  cmds.add(CMDS.COPY_MEDIA_QUERIES_TO_RESPONSIVE_STYLE, (editorInstance: Editor, sender: Component, opts: BasicComponentCommandProps & {responsiveStyles: string[]}): void => {
    const component = opts.component;
    const responsiveStyles = opts.responsiveStyles;
    const css = editorInstance.Css;

    if (!component || responsiveStyles.length === 0) {
      return;
    }

    const responsiveDeviceStylePrefix = {
      [window.PMW.emails.DEVICE_MOBILE]: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
      [window.PMW.emails.DEVICE_TABLET]: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
    };

    const cssRules = css.getRules(`#${component.getId()}`);

    Object.keys(responsiveDeviceStylePrefix).forEach((device) => {
      const deviceCssRules = cssRules.find((rule) => rule.getDevice().id === device);

      responsiveStyles.forEach((style) => {
        const ruleStyle = deviceCssRules?.getStyle(style);

        if (ruleStyle) {
          component.addStyle(`${responsiveDeviceStylePrefix[device]}${style}`, ruleStyle);
        } else {
          component.removeStyle(`${responsiveDeviceStylePrefix[device]}${style}`);
        }
      });
    });

    component.components().forEach((child) => {
      editorInstance.runCommand(CMDS.COPY_MEDIA_QUERIES_TO_RESPONSIVE_STYLE, {component: child, responsiveStyles: responsiveStyles});
    });
  });

  cmds.add(CMDS.REMOVE_MEDIA_QUERIES, (editorInstance: Editor, sender: Component, opts: {component: Component}) => {
    const comp = opts.component;
    const css = editorInstance.Css;
    if (!comp) {
      return;
    }

    const cssRules = css.getRules(`#${comp.id}`);

    cssRules.forEach((cssRule) => {
      css.remove(cssRule);
    });
  });

  cmds.add(CMDS.SET_AUTO_WIDTH_STYLE, (editorInstance: Editor, sender: Component, opts: {component: Component}) => {
    const comp = opts.component;
    if (comp) {
      comp.addClass(window.PMW.emails.AUTO_WIDTH_CTA_CLASS);

      let width = 'fit-content';
      if (comp.is(window.PMW.emails.components.text.PMW_BUTTON)) {
        width = 'auto';
        const innerTableCell = comp.find(`[data-gjs-type="${window.PMW.emails.components.table.PMW_CELL}"]`)[0];

        if (innerTableCell) {
          editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'display', value: 'table', component: comp});
          editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'width', value: width, component: innerTableCell});
        }
      }

      editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'width', value: width, component: comp});
      editorInstance.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'button-width', value: 'auto', component: comp});
      editorInstance.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
        property: 'width',
        value: width,
        id: comp.ccid,
        deviceID: window.PMW.emails.DEVICE_TABLET,
        cssPrefix: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
      });

      editorInstance.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
        property: 'width',
        value: width,
        id: comp.ccid,
        deviceID: window.PMW.emails.DEVICE_MOBILE,
        cssPrefix: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
      });
    }
  });

  cmds.add(CMDS.INIT_DROPDOWN_SUBOPTIONS, () => {
    $('.gjs-sm-pmw-dropdown-selector').each((_, el) => {
      const dropdown = $(el).find('.dropdown-selector-container');
      const showSuboption = dropdown.attr('data-suboption-show') === '1';
      const toggleProperties = JSON.parse(dropdown.attr('data-suboption-properties') || '[]') as string[];

      toggleProperties.forEach((property: string) => {
        const propertyElement = $(`.gjs-sm-property__${property}`);

        if (showSuboption) {
          propertyElement.addClass('suboption');
        } else {
          propertyElement.addClass('suboption -collapsed');
        }
      });
    });
  });

  cmds.add(CMDS.INIT_OPEN_LINK_IN_NEW_TAB_STYLE, () => {
    if ($('.js-toggle-link-new-tab').val()) {
      $(`.gjs-sm-property__pmw-open-link-in-new-tab`)?.addClass('suboption');
    }
  });

  cmds.add(CMDS.EXPAND_WIDTH_SLIDER, () => {
    const widthEl = $(`.gjs-sm-property__width`);
    widthEl?.addClass('-expand suboption');
    widthEl?.removeClass('-collapsed');
  });

  cmds.add(CMDS.SHRINK_WIDTH_SLIDER, () => {
    const widthEl = $(`.gjs-sm-property__width`);
    widthEl?.removeClass('-expand');
    widthEl?.addClass('-collapsed suboption');
  });

  cmds.add(CMDS.MAKE_STYLEMANAGER_INACTIVE, () => {
    $('.html-editor-style-manager').removeClass('-active');
    $('.js-show-hide-stylemanager-icon').removeClass('icon-sidebar-hide -make-inactive').addClass('icon-sidebar-show -make-active');

    editor.runCommand(CMDS.CLOSE_SIDE_PANEL);
    editor.runCommand(CMDS.REFRESH_STYLEMANAGER);
  });

  const scrollHandler = (): void => {
    const styleManager = $('.html-editor-style-manager');
    const header = $('.email-editor-mobile-style-header');

    const scrollPosition = styleManager.scrollTop() ?? 0;

    if (scrollPosition > 0) {
      header.addClass('-scrolling');
    } else {
      header.removeClass('-scrolling');
    }
  };

  cmds.add(CMDS.TOGGLE_STYLEMANAGER_SCROLL_LISTENER, (_: Editor, __: object, opts: {toggleOn: true}) => {
    const styleManager = $('.html-editor-style-manager');

    styleManager.off('scroll', scrollHandler);

    if (opts.toggleOn) {
      styleManager.on('scroll', scrollHandler);
    }
  });

  cmds.add(CMDS.TOGGLE_STYLEMANAGER_HEIGHT, {
    async run(editorInstance, sender, opts: {interactedEl: JQuery<HTMLElement>}) {
      let {interactedEl} = opts;
      if (interactedEl.hasClass('sp-choose') || interactedEl.hasClass('js-nav-back-to-style-list') || interactedEl.parents('.js-nav-back-to-style-list').length > 0) {
        return null;
      }

      if (interactedEl.hasClass('js-show-hide-stylemanager-icon-container')) {
        interactedEl = interactedEl.find('.js-show-hide-stylemanager-icon');
      }

      const activeToggler = $('.js-show-hide-stylemanager-icon');

      if (interactedEl.hasClass('js-show-hide-stylemanager-icon')) {
        if (interactedEl.hasClass('-make-active')) {
          editorInstance.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_ACTIVE);
        } else if (interactedEl.hasClass('-make-inactive')) {
          editorInstance.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_INACTIVE);
        }
      } else if (interactedEl.hasClass('.email-editor-mobile-style-header') || interactedEl.parents('.email-editor-mobile-style-header').length > 0) {
        if (activeToggler.hasClass('-make-active')) {
          editorInstance.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_ACTIVE);
        } else if (activeToggler.hasClass('-make-inactive')) {
          editorInstance.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_INACTIVE);
        }
      } else if (interactedEl.hasClass('.js-style-manager-container') || interactedEl.parents('.js-style-manager-container').length > 0) {
        if (activeToggler.hasClass('-make-active')) {
          editorInstance.runCommand(window.PMW.emails.commands.MAKE_STYLEMANAGER_ACTIVE);
        }
      }

      return null;
    },
    stop() {},
  });
};
